/**
 * Analytics Event Constants
 *
 * Per workspace rules: Event/property names used in 2+ places should be
 * stored in an enum or const object with UPPERCASE_WITH_UNDERSCORE naming.
 *
 * Three categories:
 * 1. ANALYTICS_EVENTS - Core funnel events (sent to BOTH GA4 and PostHog)
 * 2. PRODUCT_EVENTS - Product intelligence (PostHog only)
 * 3. ERROR_EVENTS - Error tracking (PostHog only)
 */

/**
 * Core Funnel Events
 * These events are critical for understanding the path to purchase.
 * Sent to BOTH Google Analytics 4 and PostHog.
 */
export const ANALYTICS_EVENTS = {
  /** User creates a new account (conversion) */
  SIGN_UP: 'sign_up',
  /** User logs into their account (re-engagement) */
  LOG_IN: 'log_in',
  /** User starts with a new board (top of funnel) */
  NEW_BOARD: 'new_board',
  /** User completes their creation and opens print/checkout (bottom of funnel) */
  CREATE_COMPLETE: 'create_complete',
  /** User returns to edit an existing board (re-engagement) */
  EDIT_BOARD: 'edit_board',
  /** User adds an element to the canvas */
  ELEMENT_ADDED: 'element_added',
  /** User sends a message to AI chat */
  CHAT_SENT: 'chat_sent',
  /** User performs a canvas action (undo, redo, delete, format) */
  CANVAS_ACTION: 'canvas_action',
  /** User opens checkout/purchase flow (high intent) */
  PURCHASE_STARTED: 'purchase_started',
  /** User completes a purchase (conversion) */
  PURCHASE: 'purchase',
} as const

export type AnalyticsEvent =
  (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS]

/**
 * Product Intelligence Events
 * High-volume or granular events for product debugging.
 * Sent to PostHog only (would clutter GA4).
 */
export const PRODUCT_EVENTS = {
  /** Board saved (manual or auto) */
  BOARD_SAVED: 'board_saved',
  /** Board title renamed */
  BOARD_RENAMED: 'board_renamed',
  /** Feature interaction for usage heatmaps */
  FEATURE_USED: 'feature_used',
  /** User viewed guides panel */
  GUIDES_VIEWED: 'guides_viewed',
  /** Modal opened */
  MODAL_OPENED: 'modal_opened',
  /** Modal closed */
  MODAL_CLOSED: 'modal_closed',
  /** Welcome email successfully sent to new user */
  WELCOME_EMAIL_SENT: 'welcome_email_sent',
  /** Welcome email skipped (already sent) */
  WELCOME_EMAIL_SKIPPED: 'welcome_email_skipped',
  /** Welcome email failed to send */
  WELCOME_EMAIL_FAILED: 'welcome_email_failed',
  /** Mobile user viewed chat-only experience */
  MOBILE_CHAT_ONLY_VIEWED: 'mobile_chat_only_viewed',
  /** Mobile user clicked send continue link button */
  MOBILE_CONTINUE_LINK_CLICKED: 'mobile_continue_link_clicked',
  /** Continue on desktop email sent successfully */
  MOBILE_CONTINUE_LINK_SENT: 'mobile_continue_link_sent',
  /** Continue on desktop email failed */
  MOBILE_CONTINUE_LINK_FAILED: 'mobile_continue_link_failed',
  /** User returned to desktop from mobile email */
  DESKTOP_RETURNED_FROM_MOBILE: 'desktop_returned_from_mobile',
  /** User clicked Print CTA from boards page */
  BOARDS_PRINT_CLICKED: 'boards_print_clicked',
  /** User selected a board in boards print flow */
  BOARDS_PRINT_BOARD_SELECTED: 'boards_print_board_selected',
  /** User opened fullscreen preview in boards print flow */
  BOARDS_PRINT_PREVIEW_OPENED: 'boards_print_preview_opened',
} as const

export type ProductEvent = (typeof PRODUCT_EVENTS)[keyof typeof PRODUCT_EVENTS]

/**
 * Error Events
 * Track failures and drop-offs to understand why users didn't convert.
 * Sent to PostHog only (with session replay context).
 */
export const ERROR_EVENTS = {
  /** Print validation failed (low_res, empty_canvas) */
  PRINT_VALIDATION_FAILED: 'print_validation_failed',
  /** Checkout/purchase error */
  PURCHASE_ERROR: 'purchase_error',
  /** AI generation or response error */
  AI_ERROR: 'ai_error',
  /** Canvas rendering or interaction error */
  CANVAS_ERROR: 'canvas_error',
} as const

export type ErrorEvent = (typeof ERROR_EVENTS)[keyof typeof ERROR_EVENTS]

/**
 * Element types for element_added event
 */
export const ELEMENT_TYPES = {
  TEXT: 'text',
  IMAGE: 'image',
  AI_IMAGE: 'ai_image',
} as const

export type ElementType = (typeof ELEMENT_TYPES)[keyof typeof ELEMENT_TYPES]

/**
 * Canvas action types for canvas_action event
 */
export const CANVAS_ACTIONS = {
  UNDO: 'undo',
  REDO: 'redo',
  DELETE: 'delete',
  FORMAT_TEXT: 'format_text',
  RESIZE: 'resize',
  MOVE: 'move',
} as const

export type CanvasAction = (typeof CANVAS_ACTIONS)[keyof typeof CANVAS_ACTIONS]

/**
 * Save method types for board_saved event
 */
export const SAVE_METHODS = {
  MANUAL: 'manual',
  AUTO: 'auto',
} as const

export type SaveMethod = (typeof SAVE_METHODS)[keyof typeof SAVE_METHODS]
